import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase,
  Buildings,
  Drop,
  Flag,
  Factory,
  ArrowRight,
  Tag,
  Sparkle,
} from "@phosphor-icons/react";

import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppFab } from "../components/WhatsAppFab";
import { useLang } from "../context/LanguageContext";
import {
  CLIENTS,
  SECTORS,
  portfolioStrings,
} from "../lib/portfolioData";
import { useSeo } from "../lib/useSeo";

const SECTOR_ICON = {
  government: Flag,
  oil_gas: Drop,
  diplomatic: Buildings,
  private: Factory,
  all: Briefcase,
};

const SECTOR_ACCENT = {
  government: { bg: "bg-emerald-100", text: "text-emerald-800", ring: "ring-emerald-300/60" },
  oil_gas: { bg: "bg-emerald-100", text: "text-emerald-800", ring: "ring-emerald-300/60" },
  diplomatic: { bg: "bg-emerald-100", text: "text-emerald-800", ring: "ring-emerald-300/60" },
  private: { bg: "bg-emerald-100", text: "text-emerald-800", ring: "ring-emerald-300/60" },
};

const SEO = {
  pt: {
    title: "Portefólio · Clientes Institucionais — TVCabSat Angola",
    description:
      "Veja o portefólio da TVCabSat: governos provinciais, sector petrolífero (Total, Chevron, Esso, Refinaria), embaixadas (US Embassy) e empresas privadas em Angola.",
  },
  en: {
    title: "Portfolio · Institutional Clients — TVCabSat Angola",
    description:
      "Explore TVCabSat's portfolio: provincial governments, oil & gas (Total, Chevron, Esso, Refinery), embassies (US Embassy) and private companies in Angola.",
  },
  es: {
    title: "Portafolio · Clientes Institucionales — TVCabSat Angola",
    description:
      "Descubre el portafolio de TVCabSat: gobiernos provinciales, petróleo y gas (Total, Chevron, Esso), embajadas (US Embassy) y empresas privadas en Angola.",
  },
};

const Stat = ({ value, label, testId }) => (
  <div
    data-testid={testId}
    className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition-all hover:border-blue-300 hover:shadow-xl hover:shadow-blue-600/10"
  >
    <div className="pointer-events-none absolute -right-14 -top-14 h-32 w-32 rounded-full bg-blue-500/10 blur-3xl transition-opacity group-hover:opacity-80" />
    <div className="font-heading bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-5xl font-black tracking-tighter text-transparent md:text-6xl">
      {value}
    </div>
    <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-blue-600">
      {label}
    </div>
  </div>
);

const ClientCard = ({ client, t }) => {
  const accent = SECTOR_ACCENT[client.sector] || SECTOR_ACCENT.private;
  const sector = SECTORS.find((s) => s.key === client.sector);
  const sectorLabel = sector ? sector[t.langCode] || sector.pt : "";

  return (
    <article
      data-testid={`client-card-${client.id}`}
      className="hover-lift relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 shadow-sm"
    >
      <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-blue-500/5 blur-3xl" />

      <div className="flex items-center justify-between">
        <div
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 ring-1 ${accent.bg} ${accent.ring}`}
        >
          <Tag size={11} weight="fill" className={accent.text} />
          <span
            className={`font-mono text-[9px] font-semibold uppercase tracking-[0.2em] ${accent.text}`}
          >
            {sectorLabel}
          </span>
        </div>
        <Briefcase size={18} weight="duotone" className="text-slate-300" />
      </div>

      <h3 className="font-heading mt-5 text-lg font-bold leading-snug tracking-tight text-slate-900 md:text-xl">
        {client.name}
      </h3>

      <div className="mt-5 border-t border-slate-100 pt-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
          {t.services_label}
        </div>
        <ul className="mt-3 flex flex-wrap gap-2">
          {(client.services[t.langCode] || client.services.pt).map((s) => (
            <li
              key={s}
              className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
            >
              <Sparkle size={10} weight="fill" className="text-blue-500" />
              {s}
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
};

export default function Portfolio() {
  const { lang } = useLang();
  const s = portfolioStrings[lang] || portfolioStrings.pt;
  useSeo(SEO[lang] || SEO.pt);

  const [filter, setFilter] = useState("all");

  const filtered = useMemo(
    () => (filter === "all" ? CLIENTS : CLIENTS.filter((c) => c.sector === filter)),
    [filter],
  );

  // Attach langCode helper to the strings object for the client card
  const tWithLang = { ...s, langCode: lang };

  const sectorCount = new Set(CLIENTS.map((c) => c.sector)).size;

  return (
    <div
      data-testid="portfolio-page"
      className="relative min-h-screen bg-[#a8c8e2] text-slate-900"
    >
      <Header />

      {/* Hero */}
      <section className="relative isolate overflow-hidden border-b border-slate-200 bg-gradient-to-b from-[#d6e6f2] via-[#b8d6ef] to-[#a8c8e2]">
        <div className="pointer-events-none absolute -top-32 -right-32 -z-10 h-[500px] w-[500px] rounded-full bg-blue-400/20 blur-3xl" />
        <div className="pointer-events-none absolute top-20 -left-40 -z-10 h-[400px] w-[400px] rounded-full bg-purple-400/15 blur-3xl" />
        <div className="absolute inset-0 -z-10 grid-lines opacity-50" />
        <img
          src="/bg-portfolio.png"
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover opacity-25 mix-blend-multiply"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-r from-[#d6e6f2]/85 via-[#d6e6f2]/60 to-transparent"
        />

        <div className="mx-auto max-w-[1400px] px-6 pb-20 pt-12 md:px-12 md:pt-16 lg:px-16 lg:pb-24 lg:pt-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 shadow-sm backdrop-blur-md">
            <Briefcase size={12} weight="bold" className="text-blue-600" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-600">
              {s.overline}
            </span>
          </div>

          <h1
            data-testid="portfolio-title"
            className="font-heading mt-10 max-w-4xl text-3xl font-black leading-[0.95] tracking-tighter text-slate-900 sm:text-4xl md:text-5xl lg:text-6xl"
          >
            {s.title}
          </h1>

          <p
            data-testid="portfolio-subtitle"
            className="mt-8 max-w-2xl text-base leading-relaxed text-slate-700 md:text-lg"
          >
            {s.subtitle}
          </p>

          <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-3">
            <Stat
              testId="portfolio-stat-clients"
              value={`${CLIENTS.length}+`}
              label={s.stat_clients}
            />
            <Stat
              testId="portfolio-stat-sectors"
              value={sectorCount}
              label={s.stat_sectors}
            />
            <Stat
              testId="portfolio-stat-years"
              value="20+"
              label={s.stat_years}
            />
          </div>
        </div>
      </section>

      {/* Filters + Grid */}
      <section className="relative overflow-hidden py-20 lg:py-24">
        <img
          src="/bg-portfolio.png"
          alt=""
          aria-hidden
          className="pointer-events-none absolute -right-20 top-10 -z-0 h-[80%] w-auto max-w-[50%] opacity-[0.10] mix-blend-multiply"
        />
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-blue-600">
                {s.filter_label}
              </div>
            </div>
          </div>

          <div
            data-testid="portfolio-filters"
            className="mt-6 flex flex-wrap gap-2"
          >
            {SECTORS.map((sec) => {
              const Icon = SECTOR_ICON[sec.key] || Briefcase;
              const active = filter === sec.key;
              return (
                <button
                  key={sec.key}
                  type="button"
                  onClick={() => setFilter(sec.key)}
                  data-testid={`filter-${sec.key}`}
                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                    active
                      ? "border-blue-600 bg-blue-600 text-white shadow-lg shadow-blue-600/25"
                      : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50"
                  }`}
                >
                  <Icon size={14} weight={active ? "fill" : "duotone"} />
                  {sec[lang] || sec.pt}
                  <span
                    className={`ml-1 rounded-full px-1.5 py-0.5 font-mono text-[10px] ${
                      active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {sec.key === "all"
                      ? CLIENTS.length
                      : CLIENTS.filter((c) => c.sector === sec.key).length}
                  </span>
                </button>
              );
            })}
          </div>

          {filtered.length === 0 ? (
            <div className="mt-14 rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">
              {s.no_results}
            </div>
          ) : (
            <div
              data-testid="portfolio-grid"
              className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
            >
              {filtered.map((c) => (
                <ClientCard key={c.id} client={c} t={tWithLang} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-[#d8e8f5] via-[#d8e8f5] to-blue-50 p-10 shadow-xl md:p-16">
            <div className="pointer-events-none absolute -right-32 -top-32 h-80 w-80 rounded-full bg-blue-400/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-purple-400/15 blur-3xl" />
            <div className="relative flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
              <div>
                <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
                  {s.cta_title}
                </h2>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600 md:text-base">
                  {s.cta_desc}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/#contact"
                  data-testid="portfolio-cta"
                  className="group inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:bg-blue-500 hover:shadow-blue-600/40 active:scale-95"
                >
                  {s.cta_btn}
                  <ArrowRight
                    size={16}
                    weight="bold"
                    className="transition-transform group-hover:translate-x-1"
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFab />
    </div>
  );
}
